import React, { useMemo, useRef, useState } from 'react';
import { LuDownload, LuEye, LuFile, LuFileImage } from 'react-icons/lu';
import { FaFilePdf } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SEC_ApiCall } from '../func.js';
import { PdfViewerModal } from './PdfViewerModal.js';
import './RequestFilesList.css';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
const BROWSER_OPEN_EXTENSIONS = ['pdf', ...IMAGE_EXTENSIONS];
const SIGNED_URL_CACHE_MS = 55000;
const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };

function showFileAccessError(error, actionLabel) {
  const errorCode = String(error?.message || '');
  if (errorCode === 'FILE_SCAN_PENDING') {
    toast.info('This file is still being scanned. Please try again shortly.', TOAST_CONFIG);
    return true;
  }
  if (errorCode === 'FILE_SCAN_REJECTED') {
    toast.error(
      '❌ This file failed malware/anti-virus scanning and has been deleted.',
      TOAST_CONFIG
    );
    return true;
  }

  toast.error(`❌ File could not be ${actionLabel}.`, TOAST_CONFIG);
  return false;
}

function getFileDisplayName(file) {
  return file?.oldName || file?.originalName || file?.fileName || file?.newName || 'Attached file';
}

function getFileExtension(file) {
  const fileName = getFileDisplayName(file);
  const extension = fileName.includes('.') ? fileName.split('.').pop() : '';
  return String(extension || '').toLowerCase();
}

function getFileKey(file, index, groupKey = 'main') {
  const stableFileName = file?.fileId || file?.oldName || file?.originalName || file?.fileName || file?.newName || 'file';
  return `${groupKey}-${index}-${stableFileName}`;
}

function normaliseFiles(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  if (typeof value === 'string') {
    try {
      const parsedValue = JSON.parse(value);
      return Array.isArray(parsedValue) ? parsedValue.filter(Boolean) : [];
    } catch (_) {
      return [];
    }
  }
  return [];
}

function buildSignedUrlParams({ file, folder, itemId }) {
  const params = {
    requestType: folder,
    locationId: itemId,
    fileOldName: file?.oldName || file?.originalName || file?.fileName || file?.newName,
  };
  if (file?.fileId) {
    params.fileId = file.fileId;
  }
  if (!file?.fileId && file?.newName) {
    params.objectName = file.newName;
  }
  return params;
}

// Navigate directly to the attachment URL so S3 CORS is not required for downloads.
function downloadSignedUrl(signedUrl) {
  const downloadLink = document.createElement('a');
  downloadLink.href = signedUrl;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();
}

/**
 * Shared request attachment list with clickable filenames.
 */
export function RequestFilesList({
  files,
  folder,
  itemId,
  title = 'Files',
  emptyText = '',
  groupKey = 'main',
  dispatch = null,
  className = '',
  showTitle = true,
}) {
  const [loadingActions, setLoadingActions] = useState({});
  const [previewFileState, setPreviewFileState] = useState(null);
  const signedUrlCacheRef = useRef({});

  const normalisedFiles = useMemo(() => {
    return normaliseFiles(files);
  }, [files]);

  function setActionLoading(actionKey, isLoading) {
    setLoadingActions((currentValue) => {
      const nextValue = { ...currentValue };
      if (isLoading) {
        nextValue[actionKey] = true;
      } else {
        delete nextValue[actionKey];
      }
      return nextValue;
    });
  }

  async function getSignedUrl(file, index, mode = 'inline') {
    const fileKey = getFileKey(file, index, groupKey);
    const cacheKey = `${fileKey}:${mode}`;
    const cachedEntry = signedUrlCacheRef.current[cacheKey];
    if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
      return cachedEntry.url;
    }

    const params = buildSignedUrlParams({ file, folder, itemId });
    params.downloadMode = mode === 'inline' ? 'inline' : 'attachment';
    const signedUrl = await SEC_ApiCall('general/SignedURLs', params);
    signedUrlCacheRef.current[cacheKey] = {
      url: signedUrl,
      expiresAt: Date.now() + SIGNED_URL_CACHE_MS,
    };
    return signedUrl;
  }

  async function openFile(file, index) {
    const fileKey = getFileKey(file, index, groupKey);
    const actionKey = `${fileKey}:download`;
    setActionLoading(actionKey, true);
    try {
      const signedUrl = await getSignedUrl(file, index, 'attachment');
      downloadSignedUrl(signedUrl);
    } catch (error) {
      console.error('Download failed:', error);
      const handledScanState = showFileAccessError(error, 'downloaded');
      if (dispatch && !handledScanState) {
        dispatch({
          type: 'SET_MODAL',
          payload: {
            active: true,
            apiState: 'error',
            message: 'File could not be downloaded',
          },
        });
      }
    } finally {
      setActionLoading(actionKey, false);
    }
  }

  async function openBrowserFile(file, index) {
    const fileKey = getFileKey(file, index, groupKey);
    const actionKey = `${fileKey}:preview`;
    setActionLoading(actionKey, true);
    try {
      const signedUrl = await getSignedUrl(file, index, 'inline');
      setPreviewFileState({
        displayName: getFileDisplayName(file),
        extension: getFileExtension(file),
        signedUrl,
      });
    } catch (error) {
      console.error('Failed to open signed file URL', error);
      const handledScanState = showFileAccessError(error, 'opened');
      if (dispatch && !handledScanState) {
        dispatch({
          type: 'SET_MODAL',
          payload: {
            active: true,
            apiState: 'error',
            message: 'File could not be opened',
          },
        });
      }
    } finally {
      setActionLoading(actionKey, false);
    }
  }

  function handleDownload(event, file, index) {
    event.stopPropagation();
    openFile(file, index);
  }

  if (normalisedFiles.length === 0) {
    return emptyText ? <p className="request-files-empty">{emptyText}</p> : null;
  }

  return (
    <div className={`request-files-list ${className}`.trim()}>
      {showTitle && <p className="request-files-title"><strong>{title}:</strong></p>}
      <div className="request-files-items">
        {normalisedFiles.map((file, index) => {
          const fileKey = getFileKey(file, index, groupKey);
          const displayName = getFileDisplayName(file);
          const extension = getFileExtension(file);
          const isPdf = extension === 'pdf';
          const isImage = IMAGE_EXTENSIONS.includes(extension);
          const opensInBrowser = BROWSER_OPEN_EXTENSIONS.includes(extension);
          const isPreviewLoading = Boolean(loadingActions[`${fileKey}:preview`]);
          const isDownloadLoading = Boolean(loadingActions[`${fileKey}:download`]);
          const fileContent = (
            <>
              <span className="request-file-thumb" aria-hidden="true">
                {isPdf ? (
                  <FaFilePdf />
                ) : isImage ? (
                  <LuFileImage />
                ) : (
                  <LuFile />
                )}
              </span>
              <span className="request-file-name">{displayName}</span>
              <span className="request-file-action">
                {isDownloadLoading ? <span className="spinner" /> : <LuDownload />}
              </span>
            </>
          );

          if (opensInBrowser) {
            return (
              <div
                key={fileKey}
                className="request-file-item request-file-item-static"
                title={displayName}
              >
                <span className="request-file-thumb" aria-hidden="true">
                  {isPdf ? <FaFilePdf /> : <LuFileImage />}
                </span>
                <span className="request-file-name">{displayName}</span>
                <span className="request-file-actions">
                  <button
                    type="button"
                    className="request-file-preview-action"
                    onClick={() => openBrowserFile(file, index)}
                    disabled={isPreviewLoading}
                    aria-label={`Preview ${displayName}`}
                    title={`Preview ${displayName}`}
                  >
                    {isPreviewLoading ? <span className="spinner" /> : <LuEye />}
                  </button>
                  <button
                    type="button"
                    className="request-file-download-action"
                    onClick={(event) => handleDownload(event, file, index)}
                    disabled={isDownloadLoading}
                    aria-label={`Download ${displayName}`}
                    title={`Download ${displayName}`}
                  >
                    {isDownloadLoading ? <span className="spinner" /> : <LuDownload />}
                  </button>
                </span>
              </div>
            );
          }

          return (
            <button
              key={fileKey}
              type="button"
              className="request-file-item"
              onClick={() => openFile(file, index)}
              disabled={isDownloadLoading}
              title={displayName}
            >
              {fileContent}
            </button>
          );
        })}
      </div>

      <PdfViewerModal
        isOpen={previewFileState?.extension === 'pdf'}
        pdfUrl={previewFileState?.extension === 'pdf' ? previewFileState.signedUrl : ''}
        title={previewFileState?.displayName || 'PDF preview'}
        onClose={() => setPreviewFileState(null)}
      />

      {previewFileState?.extension && previewFileState.extension !== 'pdf' && (
        <div className="request-file-preview-backdrop" role="dialog" aria-modal="true" aria-label={previewFileState.displayName}>
          <div className="request-file-preview-modal">
            <div className="request-file-preview-header">
              <button
                type="button"
                className="request-file-preview-close"
                onClick={() => setPreviewFileState(null)}
                aria-label="Close file preview"
              >
                ×
              </button>
              <p className="request-file-preview-title">{previewFileState.displayName}</p>
            </div>
            <div className="request-file-preview-body">
              <img
                src={previewFileState.signedUrl}
                alt={previewFileState.displayName}
                className="request-file-preview-image"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
